<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Api from 'dashboard/api/customers';

const emit = defineEmits(['saved']);
const { t } = useI18n();
const dialog = ref(null);
const customer = ref(null);
const query = ref('');
const options = ref([]);
const selected = ref('');
const reason = ref('');
const error = ref('');
const busy = ref(false);
const { run, abort } = useAbortableRequest();
const load = async () => {
  try {
    const response = await run(signal =>
      Api.get({ q: query.value, customer_type: 'existing_client' }, signal)
    );
    if (response)
      options.value = response.data.payload.filter(
        item => item.id !== customer.value.id
      );
  } catch (e) {
    error.value = t('CUSTOMERS.ERROR');
  }
};
watch(query, load);
const open = value => {
  customer.value = value;
  query.value = '';
  selected.value = '';
  reason.value = '';
  error.value = '';
  dialog.value.open();
  load();
};
const save = async () => {
  busy.value = true;
  try {
    const response = await Api.setReferral(customer.value.id, {
      referrer_id: selected.value,
      reason: reason.value,
    });
    emit('saved', response.data);
    dialog.value.close();
  } catch (e) {
    error.value =
      e.response?.data?.error ||
      e.response?.data?.message ||
      t('CUSTOMERS.ERROR');
  } finally {
    busy.value = false;
  }
};
defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('CUSTOMERS.SET_REFERRER')"
    :confirm-button-label="t('CUSTOMERS.SAVE')"
    :is-loading="busy"
    :disable-confirm-button="busy || !selected || !reason.trim()"
    @confirm="save"
    @close="abort"
  >
    <div class="flex flex-col gap-4">
      <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
      <Input v-model="query" :label="t('CUSTOMERS.SEARCH')" />
      <label class="flex flex-col gap-2"
        >{{ t('CUSTOMERS.REFERRED_BY') }}
        <select
          v-model="selected"
          class="rounded-lg border border-n-weak bg-n-background p-2"
        >
          <option value="">{{ t('CUSTOMERS.SELECT_REFERRER') }}</option>
          <option v-for="item in options" :key="item.id" :value="item.id">
            {{ item.name }} — {{ item.referral_code }}
          </option>
        </select>
      </label>
      <Input v-model="reason" :label="t('CUSTOMERS.REASON')" />
    </div>
  </Dialog>
</template>
